import { RefCallback, useCallback, useMemo, useRef } from 'react';

import ResizeObserver from 'resize-observer-polyfill';

export type ObserverCallback<TElement> = (elem?: TElement) => void;

interface ObserverProps<TElement> {
    callback: ObserverCallback<TElement>;
}

// https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
export const useResizeObserver = <TElement extends Element>({ callback }: ObserverProps<TElement>) => {
    const callbackRef = useRef<ObserverCallback<TElement>>();
    callbackRef.current = callback;

    const currentElem = useRef<TElement>();
    const observer = useMemo(() => {
        return new ResizeObserver(() => window.requestAnimationFrame(() => callbackRef.current?.(currentElem.current)));
    }, []);

    const refCallback: RefCallback<TElement> = useCallback(
        (element) => {
            if (currentElem.current) {
                observer.unobserve(currentElem.current);
                currentElem.current = undefined;
            }

            if (element) {
                currentElem.current = element;
                observer.observe(element);
            }
        },
        [observer]
    );
    return { refCallback, ref: currentElem };
};
